import { BRAND_DOMAIN } from '@/config/brand';
/**
 * Subdomain utilities for multi-tenant support
 * Supports both subdomain-based (org.getscopie.com) and slug-based (/org/book) access
 */

/**
 * Extract subdomain from current hostname
 * Returns null for localhost, IPs, www, or main domain
 */
export function getSubdomain(): string | null {
  const hostname = window.location.hostname;

  // Skip for localhost and IP addresses
  if (
    hostname === 'localhost' ||
    hostname.startsWith('127.0.0.1') ||
    /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname)
  ) {
    return null;
  }

  const parts = hostname.split('.');

  // Need at least 3 parts for a subdomain (subdomain.domain.tld)
  if (parts.length < 3) return null;

  // Skip 'www', 'staging' and 'app' subdomains (these are app instances, not org subdomains)
  const excludedSubdomains = ['www', 'staging', 'app'];
  if (excludedSubdomains.includes(parts[0])) return null;

  // Return the subdomain (first part)
  return parts[0];
}

/**
 * Check if current site is accessed via subdomain
 */
export function isSubdomainSite(): boolean {
  return getSubdomain() !== null;
}

/**
 * Get the main app URL (without subdomain)
 */
export function getMainAppUrl(): string {
  if (import.meta.env.VITE_ISOLATED_STAGING === 'true') return `https://staging.${BRAND_DOMAIN}`;
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  const port = window.location.port;

  // For localhost, return as-is
  if (hostname === 'localhost') {
    return port ? `${protocol}//${hostname}:${port}` : `${protocol}//${hostname}`;
  }

  // Remove subdomain if present
  const parts = hostname.split('.');
  if (parts.length >= 3 && parts[0] !== 'www') {
    // Remove subdomain, keep domain.tld
    const mainDomain = parts.slice(1).join('.');
    return `${protocol}//${mainDomain}`;
  }

  return port ? `${protocol}//${hostname}:${port}` : `${protocol}//${hostname}`;
}
